import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { 
  BookOpen, 
  Zap, 
  Globe, 
  Headphones, 
  Palette, 
  Play,
  Users,
  ChevronDown,
  Star,
  CheckCircle,
  DollarSign,
  TrendingUp,
  Award,
  Target,
  Sparkles,
  Rocket,
  Download,
  Search,
  BarChart3,
  Crown,
  Coffee,
  ArrowRight,
  Heart,
  Briefcase,
  Eye
} from 'lucide-react';

const HomePage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const { user } = useAuth();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const features = [
    {
      icon: <BookOpen className="h-12 w-12 text-green-400" />,
      title: "AI Manuscript Generation",
      description: "Create complete, engaging books in any genre. From romance to business guides - our AI writes professionally formatted manuscripts ready for publishing.",
      mockup: "/ai-manuscript-demo.png",
      buttonText: "Turn My Idea into a Book Today",
      buttonColor: "from-purple-500 to-purple-600"
    },
    {
      icon: <Palette className="h-12 w-12 text-green-400" />,
      title: "Professional KDP Covers",
      description: "Generate stunning book covers that follow exact Amazon KDP specifications. Multiple variations for A/B testing your book's success.",
      mockup: "/ai-kdp-covers.png",
      buttonText: "Launch My AI-Powered Book Instantly",
      buttonColor: "from-green-500 to-green-600"
    },
    {
      icon: <Download className="h-12 w-12 text-green-400" />,
      title: "KDP-Ready File Generation",
      description: "Automatic PDF, EPUB, and print-ready file generation. Perfect formatting for Kindle Direct Publishing with zero manual work.",
      mockup: "/kdp-file-download.png",
      buttonText: "Start Earning with My Book Now",
      buttonColor: "from-purple-500 to-purple-600"
    },
    {
      icon: <Search className="h-12 w-12 text-green-400" />,
      title: "Metadata & SEO Optimization",
      description: "AI-powered keyword research, compelling book descriptions, and category optimization to maximize your Amazon book sales.",
      mockup: "/kdp-metadata-optimizer.png",
      buttonText: "Build My KDP-Ready Book in Minutes",
      buttonColor: "from-green-500 to-green-600"
    }
  ];

  const originalFaqs = [
    {
      question: "What does AI BookMoney do?",
      answer: "AI BookMoney is an AI-powered platform that helps you create complete e-books instantly. Just provide a title and our advanced AI will generate engaging, well-structured content across multiple chapters."
    },
    {
      question: "How does the book creation process work?",
      answer: "Simply enter your book title, choose the number of chapters, select your genre, and our AI does the rest. The entire process takes just minutes to complete."
    },
    {
      question: "Can I edit my e-book after it's created?",
      answer: "Yes! Once your book is generated, you can edit, customize, and refine the content to match your vision perfectly."
    },
    {
      question: "What genres are supported?",
      answer: "We support a wide range of genres including fiction, non-fiction, business, self-help, romance, mystery, sci-fi, and many more."
    },
    {
      question: "How can my e-book be shared?",
      answer: "Your books can be exported in multiple formats (PDF, EPUB, etc.) and shared across various platforms and devices."
    }
  ];

  const creditFaqs = [
    {
      question: "How does the credit system work?",
      answer: "All plans include monthly credit allowances that renew automatically. Credits are used for AI operations: book generation (10 credits), cover creation (15 credits), chapter editing (2 credits), and metadata optimization (5 credits). All plans include access to our complete KDP Publishing Pro suite."
    },
    {
      question: "What happens if I run out of credits?",
      answer: "You can purchase additional credits at any time through our top-up option without changing your subscription plan. Credits never expire, and you maintain access to all tools regardless of your credit balance."
    },
    {
      question: "Do credits roll over to the next month?",
      answer: "Credits renew fresh each month based on your plan. Unused credits from your monthly allowance don't roll over, but any credits purchased as top-ups never expire."
    },
    {
      question: "Can I upgrade or downgrade my plan?",
      answer: "Yes! You can change your plan at any time. When upgrading, you'll immediately receive the new credit allowance. When downgrading, changes take effect at your next billing cycle."
    },
    {
      question: "What's included in all plans?",
      answer: "Every plan includes access to our complete KDP Publishing Pro suite: AI-generated manuscripts, professional cover design, metadata optimization, multi-format exports (PDF, EPUB, DOCX), and marketing tools. The only difference is your monthly credit allowance."
    },
    {
      question: "Is there a free trial?",
      answer: "New users receive 40  free trial credits to explore our platform. This is enough to generate short book(s) and test our cover and metadata tools before choosing a subscription plan."
    }
  ];

  const allFaqs = [...originalFaqs, ...creditFaqs];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
      <SEOHead 
        title="AI-Powered KDP Publishing Platform - Create & Sell Books Fast"
        description="Complete AI-powered platform for Amazon KDP publishing. Generate books, covers, and metadata 10x faster. Start your free trial with 40 credits - no credit card required."
        keywords="AI book generator, KDP publishing, Amazon self-publishing, AI writing, book creation software, passive income, make money online, AI book covers, manuscript generator"
        canonical="/"
        ogTitle="Turn AI Books Into Real Income - Complete KDP Publishing System"
        ogDescription="Join thousands earning $1000+ monthly with our AI-powered KDP publishing tools. Free trial includes 40 credits for 2-4 complete books."
        ogImage="/ai-manuscript-demo.png"
        ogUrl="/"
        schemaData={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "AI BookMoney",
          "applicationCategory": "Publishing Software",
          "operatingSystem": "Web",
          "description": "Complete AI-powered platform for Amazon KDP publishing. Generate books, covers, and metadata 10x faster.",
          "url": "https://aibookmoney.com",
          "author": {
            "@type": "Organization",
            "name": "JMFT LLC"
          },
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "description": "Free trial with 40 credits"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "reviewCount": "250"
          }
        }}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 md:pt-20 md:pb-16 lg:pt-32">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Turn AI Books Into
              <br />
              <span className="bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent">
                Real Income
              </span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto px-4">
              Complete Kindle Direct Publishing system. From AI-generated manuscripts to professional covers, 
              metadata optimization, and KDP-ready files. Start earning from your books today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8 md:mb-12">
              <Link
                to={user ? "/create" : "/register"}
                className="bg-gradient-to-r from-green-500 to-green-600 text-white px-8 py-4 rounded-full text-xl font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-200 transform hover:scale-105"
              >
                {user ? "Start Creating My Book" : "Start Creating My Book"}
              </Link>
              {user && (
                <Link
                  to="/generate"
                  className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-full text-xl font-semibold hover:bg-white hover:text-purple-900 transition-all duration-200"
                >
                  Quick Generate
                </Link>
              )}
            </div>
            
            {/* Demo Video Placeholder */}
            <div className="relative max-w-4xl mx-auto">
              <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-1 rounded-2xl">
                <div className="bg-gray-900 rounded-xl overflow-hidden">
                  <div className="aspect-video flex items-center justify-center bg-gradient-to-br from-purple-800 to-indigo-800">
                    <div className="text-center">
                      <Play className="h-16 w-16 text-white mx-auto mb-4 opacity-80" />
                      <h3 className="text-2xl font-bold text-white mb-2">FROM IDEA TO PUBLISHED BOOK IN 24 HOURS</h3>
                      <p className="text-green-200">Complete KDP Publishing System</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Stats */}
        <div className="bg-purple-800/30 backdrop-blur-sm border-y border-purple-500/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center">
              <div className="flex items-center space-x-4 bg-purple-700/50 rounded-full px-6 py-3">
                <Users className="h-6 w-6 text-purple-300" />
                <span className="text-2xl font-bold text-white">4,351+</span>
                <span className="text-purple-200">creators</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              From AI to Amazon KDP in
            </h2>
            <div className="text-6xl md:text-7xl font-bold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent mb-8">
              4 Simple Steps
            </div>
            
            {/* Process Steps */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                  1
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Generate Book</h3>
                <p className="text-gray-300">AI creates your complete manuscript with chapters</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                  2
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Design Cover</h3>
                <p className="text-gray-300">KDP-compliant covers with exact specifications</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                  3
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Optimize Metadata</h3>
                <p className="text-gray-300">AI-powered keywords, descriptions & categories</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                  4
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Publish & Profit</h3>
                <p className="text-gray-300">Download all formats and publish to Amazon</p>
              </div>
            </div>

            {/* KDP Integration Highlight */}
            <div className="mt-16 bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-2xl p-8 border border-purple-500/30">
              <h3 className="text-2xl font-bold text-white mb-4">Complete KDP Publishing Suite</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <BookOpen className="h-5 w-5 text-purple-400 mr-2" />
                    <h4 className="font-semibold text-white">Content Creation</h4>
                  </div>
                  <ul className="text-gray-300 text-sm space-y-1">
                    <li>• AI-generated manuscripts</li>
                    <li>• Chapter-by-chapter editing</li>
                    <li>• Multi-format exports (PDF, EPUB, DOCX)</li>
                  </ul>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <Palette className="h-5 w-5 text-purple-400 mr-2" />
                    <h4 className="font-semibold text-white">Professional Design</h4>
                  </div>
                  <ul className="text-gray-300 text-sm space-y-1">
                    <li>• KDP-spec compliant covers</li>
                    <li>• Genre-specific templates</li>
                    <li>• Spine width calculations</li>
                  </ul>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <Target className="h-5 w-5 text-purple-400 mr-2" />
                    <h4 className="font-semibold text-white">Marketing Optimization</h4>
                  </div>
                  <ul className="text-gray-300 text-sm space-y-1">
                    <li>• Keyword research & analysis</li>
                    <li>• Category optimization</li>
                    <li>• Pricing recommendations</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Turn any idea into an e-book
            </h2>
            <p className="text-xl text-purple-300 mb-8">With Our AI Book Generator</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {features.map((feature, index) => (
            <div key={index} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12 mb-20`}>
              <div className="lg:w-2/5 space-y-6">
                <div className="flex items-center space-x-4">
                  {feature.icon}
                  <h3 className="text-3xl font-bold text-white">{feature.title}</h3>
                </div>
                <p className="text-xl text-gray-300 leading-relaxed">
                  {feature.description}
                </p>
                <Link
                  to="/register"
                  className={`inline-block bg-gradient-to-r ${feature.buttonColor} text-white px-6 py-3 rounded-full font-semibold hover:opacity-90 transition-all duration-200 transform hover:scale-105`}
                >
                  {feature.buttonText}
                </Link>
              </div>
              <div className="lg:w-3/5">
                {feature.mockup && !feature.mockup.includes('/api/placeholder/') ? (
                  <div className="bg-gray-900 rounded-2xl overflow-hidden shadow-2xl" style={{aspectRatio: '16/10'}}>
                    <img 
                      src={feature.mockup} 
                      alt={`${feature.title} Demo`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="bg-gradient-to-br from-purple-600 to-indigo-600 rounded-2xl p-8 flex items-center justify-center" style={{aspectRatio: '16/10'}}>
                    <div className="text-center text-white">
                      <div className="text-6xl mb-4">{feature.icon}</div>
                      <p className="text-lg opacity-80">Feature Mockup</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Start Earning Section */}
      <section className="py-12 md:py-20 bg-gradient-to-r from-purple-800/30 to-indigo-800/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Ever dreamed of creating your own story?
          </h2>
          <p className="text-2xl text-purple-300 mb-12">Unleash your imagination</p>
          
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1">
              <div className="bg-gray-900 rounded-2xl overflow-hidden shadow-2xl" style={{aspectRatio: '25/16'}}>
                <img 
                  src="/interactive-book.png" 
                  alt="Interactive Book Showcase Demo"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            
            <div className="flex-1 text-left">
              <div className="bg-gray-900/50 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-green-400 mb-4">START EARNING TODAY</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-3xl font-bold text-white">Join Thousands Creating</h4>
                    <h4 className="text-3xl font-bold text-green-400">Profitable AI Books</h4>
                  </div>
                  <p className="text-gray-300">
                    Get everything you need to write, publish, and sell books on Amazon KDP using AI-powered tools.
                  </p>
                  <Link 
                    to="/register"
                    className="inline-flex items-center bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-full font-semibold hover:from-green-600 hover:to-emerald-600 transition-all duration-200"
                  >
                    Start Your Free Trial
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* KDP Process Section */}
      <section className="py-12 md:py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              From Idea to Amazon Bestseller
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto px-4">
              Our complete KDP publishing system takes you from zero to published author earning real income
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            <div className="text-center">
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <Sparkles className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">AI Generates Your Book</h3>
              <p className="text-gray-300">
                Just enter your topic and genre. Our AI creates a complete, engaging manuscript ready for publishing.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <Palette className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Professional Cover Design</h3>
              <p className="text-gray-300">
                Get multiple KDP-spec covers with exact Amazon dimensions, spine calculations, and genre-optimized designs.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <Download className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">KDP-Ready Files</h3>
              <p className="text-gray-300">
                Download perfectly formatted PDF, EPUB files plus optimized metadata and descriptions for maximum sales.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Publish & Earn</h3>
              <p className="text-gray-300">
                Upload to Amazon KDP and start earning royalties. Many of our users earn $1,000+ monthly within 90 days.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 rounded-2xl p-8 border border-green-500/30">
              <h3 className="text-2xl font-bold text-white mb-4 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 mr-2" />
                Success Stories
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 text-left">
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <p className="text-green-400 font-semibold flex items-center">
                    <DollarSign className="h-4 w-4 mr-1" />
                    $2,347 in first month
                  </p>
                  <p className="text-gray-300 text-sm">Romance series generated and published in 2 weeks</p>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <p className="text-green-400 font-semibold flex items-center">
                    <DollarSign className="h-4 w-4 mr-1" />
                    $5,890 monthly passive income
                  </p>
                  <p className="text-gray-300 text-sm">Business guide series published quarterly</p>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <p className="text-green-400 font-semibold flex items-center">
                    <DollarSign className="h-4 w-4 mr-1" />
                    $1,200+ per book
                  </p>
                  <p className="text-gray-300 text-sm">Self-help niche with optimized covers</p>
                </div>
              </div>

              {/* Flippa KDP Business Sales Section */}
              <div className="mt-8 bg-gradient-to-r from-yellow-600/20 to-orange-600/20 rounded-xl p-6 border border-yellow-500/30">
                <div className="flex items-center justify-center mb-4">
                  <Award className="h-5 w-5 text-yellow-400 mr-2" />
                  <h4 className="text-xl font-bold text-white">KDP Businesses Selling for $100K+</h4>
                </div>
                <p className="text-gray-300 text-sm mb-4 text-center">
                  Real KDP publishing businesses are actively selling on Flippa for substantial amounts
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
                  <div className="bg-gray-800/60 rounded-lg p-3">
                    <p className="text-yellow-400 font-bold text-lg">$150K+</p>
                    <p className="text-gray-300 text-xs">Established KDP portfolios</p>
                  </div>
                  <div className="bg-gray-800/60 rounded-lg p-3">
                    <p className="text-yellow-400 font-bold text-lg">$500K+</p>
                    <p className="text-gray-300 text-xs">Premium publishing brands</p>
                  </div>
                  <div className="bg-gray-800/60 rounded-lg p-3">
                    <p className="text-yellow-400 font-bold text-lg">$1M+</p>
                    <p className="text-gray-300 text-xs">Multi-genre empires</p>
                  </div>
                </div>
                <div className="text-center mt-4">
                  <a 
                    href="https://flippa.com/buy/monetization/amazon-kdp" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-yellow-400 hover:text-yellow-300 text-sm font-semibold transition-colors"
                  >
                    View Active KDP Business Sales
                    <ArrowRight className="h-3 w-3 ml-1" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Simple, Fair Pricing
            </h2>
            <p className="text-xl text-gray-300 mb-8">Start creating immediately - no tier confusion, no feature gates</p>
          </div>
          
          {/* Single Plan + Trial */}
          <div className="max-w-4xl mx-auto mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              
              {/* Free Trial */}
              <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-3xl p-8 border border-green-500/30 relative">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-green-400 to-emerald-500 text-black px-4 py-2 rounded-full text-sm font-bold flex items-center">
                    <Sparkles className="h-4 w-4 mr-1" />
                    FREE TRIAL
                  </span>
                </div>
                
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-white mb-2">Complete KDP Experience</h3>
                  <div className="text-4xl font-bold text-green-400 mb-2">40 Credits</div>
                  <p className="text-green-300 font-semibold">14 Days Free</p>
                </div>
                
                <ul className="space-y-3 mb-8 text-sm">
                  <li className="flex items-center text-gray-300">
                    <CheckCircle className="h-4 w-4 text-green-400 mr-3 flex-shrink-0" />
                    Create 2-4 complete books
                  </li>
                  <li className="flex items-center text-gray-300">
                    <CheckCircle className="h-4 w-4 text-green-400 mr-3 flex-shrink-0" />
                    Generate professional covers
                  </li>
                  <li className="flex items-center text-gray-300">
                    <CheckCircle className="h-4 w-4 text-green-400 mr-3 flex-shrink-0" />
                    AI metadata optimization
                  </li>
                  <li className="flex items-center text-gray-300">
                    <CheckCircle className="h-4 w-4 text-green-400 mr-3 flex-shrink-0" />
                    All KDP Publishing Pro tools
                  </li>
                  <li className="flex items-center text-gray-300">
                    <CheckCircle className="h-4 w-4 text-green-400 mr-3 flex-shrink-0" />
                    Multi-format downloads
                  </li>
                  <li className="flex items-center text-gray-300">
                    <CheckCircle className="h-4 w-4 text-green-400 mr-3 flex-shrink-0" />
                    No credit card required
                  </li>
                </ul>
                
                <Link
                  to="/register"
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 px-6 rounded-xl font-bold hover:from-green-600 hover:to-emerald-600 transition-all duration-200 transform hover:scale-105 block text-center"
                >
                  Start Free Trial
                </Link>
              </div>
              
              {/* Monthly Subscription */}
              <div className="bg-gradient-to-r from-purple-900/50 to-indigo-900/50 rounded-3xl p-8 border border-purple-500/50 relative">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-full text-sm font-bold">
                    BEST VALUE
                  </span>
                </div>
                
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-white mb-2">KDP Publishing Pro</h3>
                  <div className="text-4xl font-bold text-white mb-2">
                    $19<span className="text-lg text-gray-400">/month</span>
                  </div>
                  <p className="text-purple-300 font-semibold">75 Credits Included Monthly</p>
                </div>
                
                <ul className="space-y-3 mb-8 text-sm">
                  <li className="flex items-center text-gray-300">
                    <CheckCircle className="h-4 w-4 text-purple-400 mr-3 flex-shrink-0" />
                    All KDP Publishing Pro tools
                  </li>
                  <li className="flex items-center text-gray-300">
                    <CheckCircle className="h-4 w-4 text-purple-400 mr-3 flex-shrink-0" />
                    75 fresh credits every month
                  </li>
                  <li className="flex items-center text-gray-300">
                    <CheckCircle className="h-4 w-4 text-purple-400 mr-3 flex-shrink-0" />
                    4-8 complete books monthly
                  </li>
                  <li className="flex items-center text-gray-300">
                    <CheckCircle className="h-4 w-4 text-purple-400 mr-3 flex-shrink-0" />
                    Professional cover generator
                  </li>
                  <li className="flex items-center text-gray-300">
                    <CheckCircle className="h-4 w-4 text-purple-400 mr-3 flex-shrink-0" />
                    AI metadata optimization
                  </li>
                  <li className="flex items-center text-gray-300">
                    <CheckCircle className="h-4 w-4 text-purple-400 mr-3 flex-shrink-0" />
                    Top-up credits anytime
                  </li>
                </ul>
                
                <Link
                  to="/register"
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 px-6 rounded-xl font-bold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 transform hover:scale-105 block text-center"
                >
                  Subscribe Now
                </Link>
              </div>
            </div>
          </div>
          
          {/* Top-up Packages */}
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-white mb-4">Need More Credits?</h3>
            <p className="text-xl text-gray-300 mb-8">Top-up anytime - credits never expire</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Quick Boost */}
              <div className="bg-gray-900/50 rounded-2xl p-6 border border-gray-700 hover:border-purple-500/50 transition-all">
                <h4 className="text-lg font-bold text-white mb-2">Quick Boost</h4>
                <div className="text-2xl font-bold text-white mb-1">50 Credits</div>
                <div className="text-3xl font-bold text-purple-400 mb-4">$19.99</div>
                <p className="text-gray-400 text-sm mb-4">Perfect for extra projects</p>
                <Link
                  to="/credits/purchase"
                  className="w-full bg-gray-700 text-white py-2 px-4 rounded-lg font-semibold hover:bg-gray-600 transition-colors block text-center text-sm"
                >
                  Purchase
                </Link>
              </div>
              
              {/* Creator Pack */}
              <div className="bg-gray-900/50 rounded-2xl p-6 border border-purple-500 hover:border-purple-400 transition-all relative">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                    Most Popular
                  </span>
                </div>
                <h4 className="text-lg font-bold text-white mb-2">Creator Pack</h4>
                <div className="text-2xl font-bold text-white mb-1">150 Credits</div>
                <div className="text-3xl font-bold text-purple-400 mb-4">$49.99</div>
                <p className="text-gray-400 text-sm mb-4">Great for regular creators</p>
                <Link
                  to="/credits/purchase"
                  className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-purple-700 transition-colors block text-center text-sm"
                >
                  Purchase
                </Link>
              </div>
              
              {/* Pro Bundle */}
              <div className="bg-gray-900/50 rounded-2xl p-6 border border-gray-700 hover:border-purple-500/50 transition-all">
                <h4 className="text-lg font-bold text-white mb-2">Pro Bundle</h4>
                <div className="text-2xl font-bold text-white mb-1">400 Credits</div>
                <div className="text-3xl font-bold text-purple-400 mb-4">$99.99</div>
                <p className="text-gray-400 text-sm mb-4">Best value for power users</p>
                <Link
                  to="/credits/purchase"
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 px-4 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-colors block text-center text-sm"
                >
                  Purchase
                </Link>
              </div>
              
              {/* Enterprise */}
              <div className="bg-gray-900/50 rounded-2xl p-6 border border-gray-700 hover:border-purple-500/50 transition-all">
                <h4 className="text-lg font-bold text-white mb-2">Enterprise</h4>
                <div className="text-2xl font-bold text-white mb-1">1000 Credits</div>
                <div className="text-3xl font-bold text-purple-400 mb-4">$199.99</div>
                <p className="text-gray-400 text-sm mb-4">For high-volume publishing</p>
                <Link
                  to="/credits/purchase"
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 px-4 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-colors block text-center text-sm"
                >
                  Purchase
                </Link>
              </div>
            </div>
          </div>
          
          {/* MONEY MAKER KDP Publishing Pro Section - With Green/Dark Colors */}
          <div className="relative">
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 z-10">
              <span className="bg-gradient-to-r from-green-400 to-emerald-500 text-black px-6 py-2 rounded-full text-sm font-bold shadow-lg flex items-center space-x-2">
                <DollarSign className="h-4 w-4" />
                <span>Make Money With AI and KDP</span>
              </span>
            </div>
            
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-3xl p-1">
              <div className="bg-gray-900 rounded-3xl p-8 md:p-12">
                <div className="text-center mb-6 md:mb-8">
                  <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4">
                    What's Included in KDP Publishing Pro
                  </h3>
                  <p className="text-lg md:text-xl text-gray-300 mb-6 px-4">
                    Everything you need for professional Kindle Direct Publishing success
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-8">
                  <div className="text-center">
                    <div className="bg-green-600/20 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <BookOpen className="h-8 w-8 text-green-400" />
                    </div>
                    <h4 className="font-semibold text-white mb-2">Complete Content Creation</h4>
                    <ul className="text-gray-300 text-sm space-y-1">
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                        AI-generated manuscripts
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                        5-step guided book wizard
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                        Chapter-by-chapter editing
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                        Add AI chapters to existing books
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                        Professional manuscript formatting
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                        Multi-format exports (PDF, EPUB, DOCX)
                      </li>
                    </ul>
                  </div>
                  
                  <div className="text-center">
                    <div className="bg-green-600/20 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <Palette className="h-8 w-8 text-green-400" />
                    </div>
                    <h4 className="font-semibold text-white mb-2">Professional Design & Publishing</h4>
                    <ul className="text-gray-300 text-sm space-y-1">
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                        KDP-compliant cover generator
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                        Multiple cover variations & A/B testing
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                        Genre-specific templates
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                        Auto spine width calculations
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                        Print & eBook formatting
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                        Cover assignment to books
                      </li>
                    </ul>
                  </div>
                  
                  <div className="text-center">
                    <div className="bg-green-600/20 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <Target className="h-8 w-8 text-green-400" />
                    </div>
                    <h4 className="font-semibold text-white mb-2">Marketing & Business Tools</h4>
                    <ul className="text-gray-300 text-sm space-y-1">
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                        AI metadata optimization
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                        SEO keyword research & analysis
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                        Amazon category optimization
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                        Sales-optimized descriptions
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                        Competition analysis
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-400 mr-2 flex-shrink-0" />
                        KDP publishing readiness checker
                      </li>
                    </ul>
                  </div>
                </div>
                
                <div className="text-center mt-8">
                  <p className="text-gray-300 mb-4">
                    <strong className="text-green-400">Fair Usage:</strong> Pay only for what you create. 
                    Need more credits? Top-up anytime without changing your plan.
                  </p>
                  <Link
                    to="/register"
                    className="inline-flex items-center bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg"
                  >
                    Start Your Free Trial
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                  <p className="text-gray-400 text-sm mt-2">No credit card required</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Genre-Specific Resources Section - PSEO Links */}
      <section className="py-16 bg-gradient-to-r from-purple-900/30 to-indigo-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Specialized Guides for Every Genre
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Get genre-specific tips, tools, and strategies to maximize your book's success
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Romance */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-purple-500/20 hover:border-purple-500/50 transition-all duration-300">
              <div className="text-center mb-4">
                <div className="bg-gradient-to-r from-pink-500 to-rose-500 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <Heart className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white">Romance Books</h3>
              </div>
              <div className="space-y-2">
                <Link to="/romance-book-cover-generator.html" className="block text-purple-300 hover:text-purple-200 text-sm transition-colors">
                  → Romance Cover Generator
                </Link>
                <Link to="/how-to-write-romance-books.html" className="block text-purple-300 hover:text-purple-200 text-sm transition-colors">
                  → How to Write Romance
                </Link>
                <Link to="/romance-book-publishing-guide.html" className="block text-purple-300 hover:text-purple-200 text-sm transition-colors">
                  → Romance Publishing Tips
                </Link>
              </div>
            </div>

            {/* Fantasy */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-purple-500/20 hover:border-purple-500/50 transition-all duration-300">
              <div className="text-center mb-4">
                <div className="bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <Crown className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white">Fantasy Books</h3>
              </div>
              <div className="space-y-2">
                <Link to="/fantasy-book-cover-generator.html" className="block text-purple-300 hover:text-purple-200 text-sm transition-colors">
                  → Fantasy Cover Generator
                </Link>
                <Link to="/how-to-write-fantasy-books.html" className="block text-purple-300 hover:text-purple-200 text-sm transition-colors">
                  → How to Write Fantasy
                </Link>
                <Link to="/fantasy-book-publishing-guide.html" className="block text-purple-300 hover:text-purple-200 text-sm transition-colors">
                  → Fantasy Publishing Guide
                </Link>
              </div>
            </div>

            {/* Mystery */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-purple-500/20 hover:border-purple-500/50 transition-all duration-300">
              <div className="text-center mb-4">
                <div className="bg-gradient-to-r from-gray-600 to-gray-700 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <Eye className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white">Mystery Books</h3>
              </div>
              <div className="space-y-2">
                <Link to="/mystery-book-cover-generator.html" className="block text-purple-300 hover:text-purple-200 text-sm transition-colors">
                  → Mystery Cover Generator
                </Link>
                <Link to="/how-to-write-mystery-books.html" className="block text-purple-300 hover:text-purple-200 text-sm transition-colors">
                  → How to Write Mystery
                </Link>
                <Link to="/mystery-book-publishing-guide.html" className="block text-purple-300 hover:text-purple-200 text-sm transition-colors">
                  → Mystery Publishing Tips
                </Link>
              </div>
            </div>

            {/* Business */}
            <div className="bg-gray-800/50 rounded-xl p-6 border border-purple-500/20 hover:border-purple-500/50 transition-all duration-300">
              <div className="text-center mb-4">
                <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <Briefcase className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white">Business Books</h3>
              </div>
              <div className="space-y-2">
                <Link to="/business-book-cover-generator.html" className="block text-purple-300 hover:text-purple-200 text-sm transition-colors">
                  → Business Cover Generator
                </Link>
                <Link to="/how-to-write-business-books.html" className="block text-purple-300 hover:text-purple-200 text-sm transition-colors">
                  → How to Write Business Books
                </Link>
                <Link to="/business-book-publishing-guide.html" className="block text-purple-300 hover:text-purple-200 text-sm transition-colors">
                  → Business Publishing Guide
                </Link>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <p className="text-gray-400 mb-4">
              More genres: Self-Help, Cookbooks, Children's Books, Biography & more
            </p>
            <Link 
              to="/best-kdp-niches-2025" 
              className="inline-flex items-center text-purple-300 hover:text-purple-200 font-semibold transition-colors"
            >
              Explore All Niches & Genres
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white text-center mb-12">
            Questions? We have the answers
          </h2>
          <div className="space-y-4">
            {allFaqs.map((faq, index) => (
              <div key={index} className="bg-gray-900/50 rounded-lg border border-purple-500/20">
                <button
                  className="w-full text-left p-6 flex justify-between items-center focus:outline-none"
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                >
                  <span className="text-lg font-semibold text-white">{faq.question}</span>
                  <ChevronDown className={`h-5 w-5 text-purple-400 transform transition-transform ${expandedFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-300">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 bg-gradient-to-r from-purple-800/30 to-indigo-800/30 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Turn Your Ideas Into Income?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join thousands of creators earning real money with AI-powered book publishing
          </p>
          <Link
            to={user ? "/create" : "/register"}
            className="inline-block bg-gradient-to-r from-purple-500 to-pink-500 text-white px-12 py-4 rounded-full text-xl font-bold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            {user ? "Start Creating Your Book" : "Get Started Today"}
          </Link>
          <p className="text-gray-400 text-sm mt-3">No credit card required</p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;